import { watch } from 'vue'
import { useConsentStore } from '~~/stores/consentStore'
import { useAuthStore } from '~~/stores/authStore'
import { useSalesmanago } from '~~/app/composables/useSalesmanago'

/**
 * SALESmanago (sm.js) — ładowanie po zgodzie marketingowej, nadążanie za
 * tożsamością kontaktu i sprzątanie po cofnięciu zgody. Logika w
 * app/composables/useSalesmanago.ts; plugin tylko podpina ją pod sygnały ze
 * store'ów. No-op, gdy integracja nie jest włączona (brak
 * NUXT_PUBLIC_SALESMANAGO_CLIENT_ID / _ENDPOINT albo brand spoza allowlisty).
 *
 * `flush: 'post'`: GET /api/store/account/info rozstrzyga tożsamość dwoma
 * watcherami w useAuth (najpierw `isResolved`, potem `currentUser`) w tym samym
 * cyklu. Watcher „pre” mógłby wystartować bootstrap między nimi — z
 * `currentUser === null` — i sm.js załadowałby się bez `_smclientid` zalogowanego
 * klienta. Po flushu oba pola są już ustawione.
 */
export default defineNuxtPlugin(() => {
  const salesmanago = useSalesmanago()
  if (!salesmanago.enabled) return

  const consent = useConsentStore()
  const auth = useAuthStore()

  // immediate: powracający użytkownik ze zgodą dostaje sm.js na pierwszej stronie;
  // reakcja na zmianę pokrywa kliknięcie „Akceptuję” bez przeładowania.
  watch(
    () => consent.current.marketing && auth.isResolved,
    (ready) => {
      if (ready) void salesmanago.bootstrap()
    },
    { immediate: true, flush: 'post' },
  )

  // Logowanie, rejestracja, zamówienie gościa, wylogowanie.
  watch(
    () => auth.currentUser?.id ?? null,
    () => salesmanago.onUserChanged(),
    { flush: 'post' },
  )

  // Tylko przejście true → false. Bez `immediate` — wejście użytkownika, który
  // zgody nigdy nie dał, nie jest cofnięciem i niczego nie kasuje.
  watch(
    () => consent.current.marketing,
    (granted, wasGranted) => {
      if (wasGranted && !granted) salesmanago.onConsentRevoked()
    },
    { flush: 'post' },
  )
})
